/**
 * Contract Urgency Badge Component
 * US-CONT-005: View Expiring Contracts Alert
 * Displays urgency level as colored badge with optional label
 */

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ContractUrgency } from '../types/expiringContracts.types';
import { formatDaysRemainingShort } from '../utils/contractFormatters.utils';
import { getUrgencyColors, getUrgencyLabel } from '../utils/contractUrgency.utils';

interface ContractUrgencyBadgeProps {
  urgency: ContractUrgency;
  daysRemaining: number;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Badge size variants
 */
const sizeVariants = {
  sm: 'text-xs px-2 py-0.5',
  md: 'text-sm px-2.5 py-1',
  lg: 'text-base px-3 py-1.5'
};

export function ContractUrgencyBadge({
  urgency,
  daysRemaining,
  showLabel = true,
  size = 'md',
  className
}: ContractUrgencyBadgeProps) {
  const colors = getUrgencyColors(urgency);
  const label = getUrgencyLabel(urgency);
  const daysText = formatDaysRemainingShort(daysRemaining);

  return (
    <Badge
      variant={colors.badge as 'default' | 'secondary' | 'destructive' | 'outline'}
      className={cn(
        'font-medium',
        sizeVariants[size],
        colors.bg,
        colors.text,
        colors.border,
        className
      )}
    >
      {showLabel ? (
        <span className="flex items-center gap-1.5">
          <span>{label}</span>
          <span className="opacity-75">•</span>
          <span>{daysText}</span>
        </span>
      ) : (
        daysText
      )}
    </Badge>
  );
}

/**
 * Compact urgency badge (icon + days only)
 */
interface CompactUrgencyBadgeProps {
  urgency: ContractUrgency;
  daysRemaining: number;
  className?: string;
}

export function CompactUrgencyBadge({
  urgency,
  daysRemaining,
  className
}: CompactUrgencyBadgeProps) {
  return (
    <ContractUrgencyBadge
      urgency={urgency}
      daysRemaining={daysRemaining}
      showLabel={false}
      size="sm"
      className={className}
    />
  );
}

/**
 * Urgency indicator dot (minimal version)
 */
interface UrgencyIndicatorProps {
  urgency: ContractUrgency;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const dotSizes = {
  sm: 'w-2 h-2',
  md: 'w-3 h-3',
  lg: 'w-4 h-4'
};

export function UrgencyIndicator({
  urgency,
  size = 'md',
  className
}: UrgencyIndicatorProps) {
  const colors = getUrgencyColors(urgency);

  return (
    <span
      className={cn(
        'inline-block rounded-full',
        dotSizes[size],
        colors.bg,
        colors.border,
        'border-2',
        className
      )}
      title={getUrgencyLabel(urgency)}
      aria-label={`${getUrgencyLabel(urgency)} urgency`}
    />
  );
}
